import { useRouter } from 'next/router'
import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import MapSearch from 'modules/MapSearch'

import ErrorBox from 'components/ErrorBox'
import LoadingBox from 'components/LoadingBox'
import Spacer from 'components/Spacer'

import useWindowSize from 'services/hooks/useWindowSize'
import { useListingsWithinLocation } from 'services/swr/listings/useListingsWithinLocation'

import Filters from './Filters'
import ListingResultsGrid from './ListingResultsGrid'
import MapListModeButton from './MapListModeButton'
import SearchInput from './SearchInput'
import SelectedListingOverlay from './SelectedListingOverlay'
import { IQuery, ISort } from './types'
import useDisplayListings from './utils/hooks/useDisplayListings'
import useGeocodedPlace from './utils/hooks/useGeocodedPlace'
import useLocation from './utils/hooks/useLocation'
import useLocationSearch from './utils/hooks/useLocationSearch'
import useMapBounds from './utils/hooks/useMapBounds'
import useSelectedListing from './utils/hooks/useSelectedListing'
import useWindowHeightCorrection from './utils/hooks/useWindowHeightCorrection'
import useZoomLevel from './utils/hooks/useZoomLevel'

const NAVIGATION_HEIGHT = 70
const SEARCH_FILTER_BAR_HEIGHT = 65
const STACKED_SEARCH_FILTER_BAR_HEIGHT = 127

const OFFSET_TOP = NAVIGATION_HEIGHT + SEARCH_FILTER_BAR_HEIGHT
const STACKED_OFFSET_TOP = NAVIGATION_HEIGHT + STACKED_SEARCH_FILTER_BAR_HEIGHT

const RESULTS_GRID_WIDTH = 680

const Container = styled.div``

const MenuContainer = styled.div`
  background: ${props => props.theme.colors.white};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.colors.neutral100};
  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    flex-direction: row;
  }
`

const ContentContainer = styled.div`
  position: relative;
  height: calc(100vh - ${STACKED_OFFSET_TOP}px);
  width: 100vw;

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    height: calc(100vh - ${OFFSET_TOP}px);
  }
`

const MapContainer = styled.div`
  height: calc(100vh - ${STACKED_OFFSET_TOP}px);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: calc(100vw - ${props => props.theme.metrics.smallPhone}px);
  }

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    height: calc(100vh - ${OFFSET_TOP}px);
    width: calc(100vw - ${RESULTS_GRID_WIDTH}px);
  }
`

interface IProps {
  ipLatitude: number | undefined
  ipLongitude: number | undefined
}

const HomesForSale: FunctionComponent<IProps> = ({ ipLatitude, ipLongitude }) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isMobile = !!width && width < theme.metrics.tablet
  const isDesktopMedium = !!width && width >= theme.metrics.desktopMedium

  /**
   * Mobile phones don't play nice with our 100vh html & body implementation.
   *
   * This hook will correct the height of the map container in JS so that we don't have to change
   * the global structure of our CSS in order to ship this feature
   * */
  const { CONTENT_CONTAINER_ID, MAP_CONTAINER_ID } = useWindowHeightCorrection({
    isMobile,
    isDesktopMedium,
    mobileHeaderHeight: OFFSET_TOP,
  })

  /* Mobile mode toggle state */
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map')
  const isInMobileListViewMode = isMobile && viewMode === 'list'
  const toggleViewMode = () => setViewMode(viewMode === 'map' ? 'list' : 'map')

  /**
   * Text search state
   *
   * For v1, we use Google Maps API geocoding feature to convert a string address into a google
   * place object with coordinates we can use to move the map center
   *
   * In v2, we'll use Places Autocomplete to get pre-search suggestions and convert the user selection
   * to a google place object in order provide a better user experience
   * */
  const {
    address,
    handleSetAddress,
    searchByAddress,
    foundSearchLocationCenter,
    foundSearchLocationGeocodedPlace,
    searchType,
    resetSearchType,
    ...noResultsModalProps
  } = useLocationSearch()

  /**
   * Selected listing overlay state
   *
   * User can display a listing in an overlay by clicking on a listing in the map or list view
   * */
  const { handleHighlightedListingChange, ...selectedListingProps } = useSelectedListing()

  /**
   * Map Bounds state
   *
   * When the user moves the map, the coordinates of the boundaries (outer edge of the map) change.
   * We use the updated bounds to re-fetch the listings within the new rectangular area
   */
  const { mapBounds, mapBoundsCenter, onMapBoundsChange } = useMapBounds()

  /**
   * Location state
   *
   * We use the user's browser location (or IP address as fallback) to get their approximate
   * initial location and center the map there to start
   *
   * This value changes when user
   * - moves the map
   * - submits a location search in the search bar
   */
  const { location } = useLocation(
    { ipLatitude, ipLongitude },
    mapBoundsCenter,
    foundSearchLocationCenter,
  )

  /**
   * Geocoded Place state
   *
   * When the user searches in the text search, we use the Google Maps API to convert the string
   * address into a google place object with coordinates we can use to move the map center
   */
  const { formattedPlaceName } = useGeocodedPlace(location, foundSearchLocationGeocodedPlace)

  /**
   * Zoom state
   *
   * Right now, we only use this to auto-zoom in when the user searches for an exact address.
   *
   * In the near future we will also use this to
   * - zoom out in the "no results" case until we can show some listings on screen
   * - zoom in on listings when the user clicks on a listing in the list view
   */
  const { zoom, onZoomChange } = useZoomLevel(searchType)

  // when the user zooms out after searching for an exact address, we reset the search type
  // so if they search again, we can zoom in again
  useEffect(resetSearchType, [zoom])

  /**
   * Our query to search for listings
   * - within our map bounds
   * - with the user's selected filters
   * - sorted by the user's selected sort
   * */
  const [query, setQuery] = useState<Partial<IQuery>>({})

  const onChangeFilter = (queries: Partial<IQuery>) => setQuery({ ...query, ...queries })

  // we sync the user's selections so they can share their search as a link
  const { query: routerQuery } = useRouter()

  useEffect(() => {
    if (routerQuery) setQuery(routerQuery as Partial<IQuery>)
  }, [routerQuery])

  // sort
  const [sort, setSort] = useState<ISort>({
    key: 'featured',
    label: 'Featured',
  })

  const [sortActive, setSortActive] = useState(false)

  const handleChangeSort = (newSort: ISort) => {
    setSort(newSort)
    setSortActive(true)
  }

  const {
    foundListings: mapListings,
    count,
    error: listingSearchError,
  } = useListingsWithinLocation({
    coordinates: mapBounds,
    query,
    sortKey: sort.key,
    sortDirection: sort.direction,
  })

  /** We store a ref to the previously found listing to prevent data from blinking during loading */
  const displayListings = useDisplayListings(mapListings)

  // must be initialized or UI will show wrong UI to start/can get stuck
  if (!width) return null

  return (
    <Container>
      <MenuContainer>
        <SearchInput
          address={address}
          onChange={handleSetAddress}
          onSubmit={searchByAddress}
          {...noResultsModalProps}
        />
        <Spacer size={24} />
        <Filters onChange={onChangeFilter} />
      </MenuContainer>
      <ContentContainer id={CONTENT_CONTAINER_ID}>
        {listingSearchError && <ErrorBox error={listingSearchError} />}
        {!listingSearchError && (
          <MapContainer id={MAP_CONTAINER_ID}>
            {location?.latitude && location?.longitude
              ? (
                <MapSearch
                  initialCenter={location}
                  listings={displayListings || []}
                  onMapBoundsChange={onMapBoundsChange}
                  setHighlightedListingId={handleHighlightedListingChange}
                  onZoomChange={onZoomChange}
                  zoom={zoom}
                />
              )
              : (
                <LoadingBox />
              )}
          </MapContainer>
        )}
        {(!isMobile || isInMobileListViewMode) && (
          <ListingResultsGrid
            address={formattedPlaceName}
            listings={displayListings || []}
            initializing={!displayListings}
            count={count}
            sort={sort}
            sortActive={sortActive}
            onChangeSort={handleChangeSort}
            setHighlightedListingId={handleHighlightedListingChange}
          />
        )}
        {isMobile && (
          <MapListModeButton
            toggleMode={toggleViewMode}
            mode={viewMode}
          />
        )}
      </ContentContainer>
      <SelectedListingOverlay {...selectedListingProps} />
    </Container>
  )
}

export default HomesForSale
